<template>
  <header id="header" class="header" role="banner">
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <!-- Always present: reserves space so scroll doesn't jump -->
    <div
      class="alert-banner-wrapper"
      :style="alertHeightStyle"
      ref="alertWrapper"
    >
      <transition
        name="fade-slide"
        @after-enter="delayedUpdateTopHeight"
        @after-leave="delayedUpdateTopHeight"
      >
        <AlertBanner
          v-if="
            !isScrolled &&
            property?.alert_display_type !== 'none' &&
            property.alert_message
          "
          aria-live="polite"
          :property="property"
          ref="alertRef"
          id="alert"
        />
      </transition>
    </div>

    <section
      id="top"
      ref="topSection"
      :class="[
        'nav-container fixed-top-block',
        {
          scrolled: isScrolled,
          home: isHome,
          'no-alert': property?.alert_display_type === 'none',
          'has-alert': property?.alert_display_type !== 'none',
        },
      ]"
    >
      <div class="top-nav" :class="{ container: isDesktop }">
        <div class="main-logo" v-if="isDesktop">
          <NuxtLink
            to="/"
            :title="propertyData.name"
            :aria-label="`Go to ${propertyData.name} home page`"
          >
            <NuxtImg
              class="mall-logo"
              src="/Images/logos/hpv_white.png"
              :alt="propertyData.name"
              :modifiers="{ format: 'webp' }" 
            />
          </NuxtLink>
        </div>

        <div class="top-nav-utilities" v-if="isDesktop">
          <div class="g-language-selector">
            <LanguageSelector />
          </div>

          <NuxtLink class="search" to="/search">
            <FontAwesomeIcon class="fa-icon" :icon="['fas', 'search']" />
            Search
          </NuxtLink>

          <NuxtLink class="gift-card-btn" to="/gift-card">Gift Card</NuxtLink>
        </div>

        <div class="main-nav-wrapper" :class="{ open: showMobileMenu }" v-else>
          <div class="container d-flex">
            <div class="main-nav">
              <div class="spacer"></div>

              <div class="mall-logo m-0">
                <NuxtLink
                  to="/"
                  :aria-label="`Go to ${propertyData.name} home page`"
                  :title="propertyData.name"
                >
                  <NuxtImg
                    src="/Images/logos/hvp_logo_mobile.png"
                    :alt="propertyData.name"
                    width="300"
                    height="105"
                    :modifiers="{ format: 'webp' }" 
                  />
                </NuxtLink>
              </div>

              <div
                id="menu-icon"
                role="button"
                tabindex="0"
                :aria-label="showMobileMenu ? 'Close menu' : 'Open menu'"
                :aria-expanded="showMobileMenu.toString()"
                @click.prevent="showMobileMenu = !showMobileMenu"
                @keydown.enter.prevent="showMobileMenu = !showMobileMenu"
                @keydown.space.prevent="showMobileMenu = !showMobileMenu"
                :class="{ open: showMobileMenu }"
              >
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <transition name="slide">
          <div
            v-if="showMobileMenu"
            class="mobile-nav"
            :style="{
              top: headerHeight + 'px',
              height: `calc(100vh - ${headerHeight}px)`,
            }"
          >
            <nav
              v-if="mobileMenu"
              role="navigation"
              aria-label="Main mobile menu"
            >
              <ul>
                <li>
                  <NuxtLink
                    class="menu-item"
                    target="_blank"
                    :aria-label="'Go to Home Page'"
                    :aria-current="route.path === '/' ? 'page' : null"
                    to="/"
                    @click="closeMobileMenu()"
                  >
                    Home
                  </NuxtLink>
                </li>

                <li v-for="(item, index) in mobileMenu" :key="index">
                  <!-- External, no sub_menu -->
                  <div
                    @click="closeMobileMenu()"
                    v-if="!item.sub_menu && item.target"
                  >
                    <a
                      class="menu-item"
                      target="_blank"
                      :href="item.href"
                      :aria-label="`Go to ${item.name} external page. New window will open.`"
                    >
                      {{ item.name }}
                    </a>
                  </div>

                  <!-- Internal, no sub_menu -->
                  <div
                    @click="closeMobileMenu()"
                    v-else-if="!item.sub_menu && !item.target"
                  >
                    <NuxtLink
                      class="menu-item"
                      :to="item.href"
                      :aria-label="`Go to ${item.name} page.`"
                    >
                      {{ item.name }}
                    </NuxtLink>
                  </div>

                  <!-- Submenu: NuxtLink shown + submenu always open -->
                  <div v-else>
                    <NuxtLink
                      v-if="item.href"
                      class="menu-item"
                      :aria-label="`Go to ${item.name} page.`"
                      :to="item.href"
                      @click="closeMobileMenu()"
                    >
                      {{ item.name }}
                    </NuxtLink>
                    <span v-else class="menu-item">{{ item.name }}</span>

                    <div :id="'submenu-' + index">
                      <div
                        v-for="(sub_menu, subIndex) in item.sub_menu"
                        :key="subIndex"
                        @click="closeMobileMenu()"
                      >
                        <a
                          v-if="sub_menu.target"
                          :href="sub_menu.href"
                          :aria-label="`Go to ${sub_menu.name} external page. New window will open.`"
                          class="menu-item sub"
                          target="_blank"
                        >
                          <span class="hvr-underline-from-center">
                            {{ sub_menu.name }}
                          </span>
                        </a>
                        <NuxtLink
                          v-else-if="sub_menu.href"
                          :to="sub_menu.href"
                          :aria-label="`Go to ${sub_menu.name} page.`"
                          class="menu-item sub"
                          tabindex="0"
                        >
                          <span class="hvr-underline-from-center">
                            {{ sub_menu.name }}
                          </span>
                        </NuxtLink>

                        <NuxtLink
                          v-else
                          :aria-label="`Go to ${sub_menu.name} page.`"
                          :to="sub_menu.route_name"
                          class="menu-item sub"
                          tabindex="0"
                        >
                          <span class="hvr-underline-from-center">
                            {{ sub_menu.name }}
                          </span>
                        </NuxtLink>
                      </div>
                    </div>
                  </div>
                </li>

                <li>
                  <NuxtLink
                    class="menu-item"
                    target="_blank"
                    to="/search"
                    :aria-label="`Go to Search page.`"
                  >
                    Search
                  </NuxtLink>
                </li>

                <li>
                  <NuxtLink  class="menu-item" to="/gift-card" :aria-label="`Go to gift card page.`">Gift Card</NuxtLink>
                </li>

                <LanguageSelector :showMenu="showMenu" />
                <PropertySocialLinks :type="property" :has_comment="true" />
              </ul>
            </nav>
          </div>
        </transition>
      </div>
    </section>

    <section
      v-if="route.path === '/'"
      class="hero-section"
      :style="{
        marginTop:
          property.alert_display_type == 'none' && isHome && !isDesktop
            ? topHeight + 'px'
            : '0px',
      }"
    >
      <HeroComponent />
    </section>

    <section>
      <HeaderMenu :menuItems="menuItems" />
    </section>
  </header>
</template>


<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useRoute } from "vue-router";
import { useMainStore } from "~/store";
const { isScrolled } = useScroll();

// Props
const props = defineProps([
  "menuItems",
  "property",
  "mobileMenu",
  "searchItems",
  "hoursText",
  "hours",
  "showSearch",
]);

// Emits
const emit = defineEmits(["update-transparency", "update-header-height", "update-alert"]);

// Utilities
const { isMobile, isTablet, isDesktop } = useMobileChecker();
const route = useRoute();
const store = useMainStore();
const { getDataSets, propertyData, property, loadPageData, api, baseURL } =
  store;

// State
const showMenu = ref(false);
const showMobileMenu = ref(false);
const showSubMenuItem = ref(false);
const subMenuID = ref("");
const headerHeight = ref(0);
const topSection = ref(null);
const topHeight = ref(0);
const alertRef = ref(null);
const alertHeight = ref(0);
const alertWrapper = ref(null)

// Computed
const isHome = computed(() => route.path === "/");

// Methods
function updateTopHeight() {
  nextTick(() => {
    requestAnimationFrame(() => {
      const header = document.querySelector("#top");
      const alert = document.querySelector("#alert");

      // Use getBoundingClientRect for accurate dimensions post-paint
      const alertHeightVal = alert?.getBoundingClientRect().height || 0;
      const headerHeightVal = header?.getBoundingClientRect().height || 0;

      alertHeight.value = alertHeightVal;
      headerHeight.value = headerHeightVal + alertHeightVal;
      emit("update-header-height", headerHeight.value);
    });
  });
}

function delayedUpdateTopHeight() {
  requestAnimationFrame(() => {
    setTimeout(() => {
      updateTopHeight();
    }, 500);
  });
}

const closeMobileMenu = () => {
  showMobileMenu.value = false;
};

const hideSubMenu = () => {
  subMenuID.value = "";
};

// Watchers
watch(
  () => route.fullPath,
  () => {
    if (isMobile) showMenu.value = false;
    showMobileMenu.value = false;
  }
);

watch(isScrolled, () => {
  nextTick(() => {
    requestAnimationFrame(updateTopHeight);
  });
});

watch(() => showMobileMenu, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      const firstMenuItem = document.querySelector('.mobile-nav a, .mobile-nav button');
      firstMenuItem?.focus();
    });
  }
});

watch(alertHeight, (val) => {
  document.documentElement.style.setProperty("--alert-height", `${val}px`);
});

// Lifecycle
onMounted(() => {
  nextTick(() => {
    requestAnimationFrame(() => {
      const header = document.querySelector("#top");
      const alert = document.querySelector("#alert");
      if (header && alert) {
        headerHeight.value = header.offsetHeight + alert.offsetHeight;
        alertHeight.value = alert.offsetHeight;
        emit("update-header-height", headerHeight.value);
      } else if (header) {
        headerHeight.value = header.offsetHeight;
        emit("update-header-height", headerHeight.value);
      }
      if (alertWrapper.value) {
        const height = alertWrapper.value.offsetHeight
        emit('update-alert', height)
      }

      // Attach resize handler
      window.addEventListener("resize", updateTopHeight);
    });
  });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateTopHeight);
});

const alertHeightStyle = computed(() => {
  return alertHeight.value ? { height: `var(--alert-height)` } : {}
})
</script>


<style lang="scss" scoped>
.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: #000;
  color: #fff;
  padding: 8px;
  z-index: 1000;
  &:focus {
    top: 0;
  }
}

.header {
  .alert-banner-wrapper {
    min-height: 51px;
    transition: height 0.3s ease;
    overflow: visible;

    #alert {
      box-sizing: border-box;
    }

    & > * {
      height: 100%;
    }

    @media only screen and (max-width: 768px) {
      min-height: 60px;
    }
  }

  #top {
    line-height: 52px;
    height: 52px;
    background-color: $black;
    color: $white;
    transition: all ease 0.3s;

    &.fixed-top-block {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 1000;
    }

    &.has-alert {
      position: unset !important;
      transition: all 0.3s ease;
    }

    &.scrolled {
      position: fixed !important;
      transition: all 0.3s ease;
    }

    a {
      color: $white;
    }

    .top-nav {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .main-logo {
        img {
          max-width: 315px;
        }
      }

      .top-nav-utilities {
        display: flex;
        justify-content: space-between;
        text-transform: uppercase;
        align-items: center;
        height: auto;
        font-size: 10px;
        line-height: 10px;
        letter-spacing: 1px;
        font-family: $font_poppin;

        > * {
          padding: 0 20px;
        }

        > *:not(:last-child) {
          position: relative;
        }

        .search {
          height: auto;
          width: 1;
          border-left: 1px solid $white;
          border-right: 1px solid $white; // ADJUSTMENT - reinstate once gift card is added back in
          padding-top: 6px;
          padding-bottom: 6px;
          color: $white !important;

          .fa-icon {
            color: #a4a3a3;
            font-weight: 900;
            padding-right: 10px;
          }
        }

        .gift-card-btn {
          color: $white !important;
        }
      }
    }
  }

  // MOBILE
  @media only screen and (max-width: 1024px) {
    width: 100%;
    transition: all 0.3s;
    #top {
      height: 128px;
      padding: 10px;
      line-height: 35px;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s ease;
      width: 100vw;

      &.fixed-top-block {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 1000;
      }

      &.has-alert {
        position: unset !important;
        transition: all 0.3s ease;
      }

      &.scrolled {
        position: fixed !important;
        transition: all 0.3s ease;
      }

      .hero-section {
        transition: margin-top 0.3s ease;
      }

      .top-nav {
        margin: 0;
        padding: 0;
        box-sizing: border-box;

        .main-nav {
          display: flex;
          justify-content: center;
          align-items: center;

          width: 90vw; /* Ensure it takes full width */
          position: relative;
          padding: 0 20px;

          .spacer {
            width: 0px;
            flex-shrink: 0;
          }

          .mall-logo {
            flex-grow: 1;
            text-align: center;
            display: flex;
            justify-content: center;
            align-items: center;

            a {
              display: inline-block;
            }

            img {
              margin: 0 0 0 0px !important;
              max-width: 295px !important;
              height: auto !important;
              width: 84% !important;

              @media only screen and (max-width: 580px) {
                width: 80% !important;
                max-width: 275px !important;
              }
            }
          }

          #menu-icon {
            position: absolute;
            right: 10px;
            top: 10px;
            display: flex;
            flex-shrink: 0;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            z-index: 9;

            span {
              position: absolute;
              height: 2.5px;
              width: 1.5rem;
              background: $white;
              transition: 0.5s ease-in-out;
              border-radius: 0;

              &:nth-child(1) {
                top: -3px;
              }
              &:nth-child(2),
              &:nth-child(3) {
                top: 5px;
              }
              &:nth-child(4) {
                top: 13px;
              }
            }

            &.open {
              span {
                background: $white;
                width: 1.5rem;
              }

              span:nth-child(1),
              span:nth-child(4) {
                top: 5px;
                width: 0%;
              }

              span:nth-child(2) {
                transform: rotate(45deg);
              }

              span:nth-child(3) {
                transform: rotate(-45deg);
              }
            }
          }
        }

        @keyframes menu-slide {
          from {
            transform: translateX(-100%);
          }

          to {
            transform: translateX(0%);
          }
        }

        // Slide-in animation when menu opens
        .slide-enter-active {
          animation: menu-slide 0.3s ease-out forwards;
        }

        // Slide-out animation when menu closes
        .slide-leave-active {
          animation: menu-slide 0.3s ease-in reverse forwards;
        }

        .mobile-nav {
          top: var(--header-height) !important;
          left: 0;
          position: fixed;
          z-index: 9998;
          width: 80%;
          height: calc(100% - 128px) !important;
          padding: 1.5em 0 43px 2em;
          background-color: $white;
          font-family: $font_poppin;
          border-right: 1px solid $black;
          box-sizing: border-box !important;
          list-style: none;

          nav {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            height: 100%;
            max-width: 100%;
            position: relative;
            overflow: hidden;
          }

          ul {
            list-style-type: none;
            padding: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            font-family: $font_poppin;
            font-size: 14px;
            overflow-y: auto;
            overflow-x: hidden;
            padding-right: 10px;
            width: 100%;
          }

          .mall-logo {
            width: 11rem;
            padding-top: 20px;
            filter: invert(0);
          }

          .menu-item {
            padding: 0em 0;
            font-family: $font_poppin !important;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            display: inline-block;
            color: $black;
            font-weight: 500;
            border-bottom: none;

            &.sub {
              padding-left: 14px;

              &::before {
                content: "—";
                font-size: 5px;
                vertical-align: middle;
                padding-right: 5px;
              }
            }

            &.collapsed {
              color: $black;
            }

            .fal {
              font-size: 1.75rem;
            }
          }

          .accordion-body {
            text-align: center;

            a {
              font-size: 1.5rem;
              text-transform: initial;
            }
          }

          .default-btn {
            color: #3c3c3c;
            font-size: 1rem;
            line-height: 20px;
            width: 345px;
            margin: 15px auto;

            .fa-search {
              margin-left: 10px;
            }
          }

          .social-links-container {
            padding-top: 5px;
            justify-content: flex-start;
            max-width: 345px;

            a {
              padding: 5px 20px;
            }

            [class*="fa-"] {
              font-size: 1.75rem;
            }
          }
        }

        // Optional: Chevron rotation for menu toggle
        .mobile-menu-icon.open {
          .fa-chevron-down {
            transform: rotate(180deg);
            transition: transform 0.3s ease;
          }
        }
      }
    }
  }
}
</style>